import React, { useState, useEffect } from 'react';
import UDIRowPopup, { UDIPopupField, UDIAddRowButton, useUDIRowPopup } from './UDIRowPopup.js';

export const CRITICAL_WARNING_OTHER = "CW999";

const hasSpecificLanguage = (desc) => {
    const lang = (desc?.Description?.Language?.Value || "").trim();
    return lang !== "" && lang.toUpperCase() !== "ANY";
};

// CW999 (OTHER) needs at least one comment and every comment in a specific language (not ANY)
export const otherWarningError = (descriptions) => {
    const list = descriptions || [];
    if (list.length === 0)
        return "CW999 (OTHER) requires at least one Description.";
    if (!list.every(hasSpecificLanguage))
        return "CW999 (OTHER) Descriptions must all have a specific language (not ANY).";
    return null;
};

export const findInvalidOtherWarnings = (items) => {
    let list = items;
    if (typeof list === 'string') {
        try { list = JSON.parse(list); } catch { list = []; }
    }
    return (Array.isArray(list) ? list : []).filter(item => item.Type?.Value === CRITICAL_WARNING_OTHER && otherWarningError(item.Descriptions));
};

const CriticalWarningsOrContraindications = ({
    UDI,
    setUDI,
    initialData = '[]',
    hiddenFieldName = "Form.EUProductUDI.ListOfCriticalWarningsOrContraindications",
    fieldTitle = "List of Critical Warnings or Contraindications",
    forceIsEditing = null,
    validationTrigger = 0
}) => {
    // --- Constants ---
    const cwctype = [
        { key: "CW001", value: "Do not resterilize" },
        { key: "CW006", value: "Defibrillation-proof type B applied part" },
        { key: "CW007", value: "Do not use if package is damaged" },
        { key: "CW008", value: "Biological risks" },
        { key: "CW009", value: "Do not re-use" },
        { key: "CW010", value: "Consult instructions for use" },
        { key: "CW011", value: "Caution" },
        { key: "CW012", value: "Caution: Electrostatic sensitive, do not touch directly." },
        { key: "CW013", value: "Radiation of laser apparatus" },
        { key: "CW014", value: "Type BF applied part" },
        { key: "CW015", value: "Defibrillation-proof type BF applied part" },
        { key: "CW016", value: "Type B applied part" },
        { key: "CW017", value: "Defibrillation-proof type CF applied part" },
        { key: "CW018", value: "Refer to instruction manual/booklet" },
        { key: "CW020", value: "Do not sterilize" },
        { key: "CW022", value: "Do not cut" },
        { key: "CW023", value: "Consider hazards from magnetic fields" },
        { key: "CW024", value: "Do not store near magnets or magnetic devices" },
        { key: "CW025", value: "MR SAFE" },
        { key: "CW026", value: "MR CONDITIONAL" },
        { key: "CW027", value: "MR UNSAFE" },
        { key: "CW028", value: "Exposure to radiation when using the device" },
        { key: "CW029", value: "Caution, sharp edges" },
        { key: "CW030", value: "Consult healthcare professional before use" },
        { key: "CW031", value: "Implant timeframe limitations" },
        { key: "CW032", value: "Do not use past indicated date" },
        { key: "CW035", value: "Restriction in use of des infectors," },
        { key: "CW036", value: "Restrictions in use for combination of other devices" },
        { key: "CW037", value: "GHS01 - Danger - Unstable, Explosive" },
        { key: "CW038", value: "GHS02 - Danger or Warning - Flammable" },
        { key: "CW039", value: "GHS03- Danger or Warning -Oxidising" },
        { key: "CW040", value: "GHS04- Warning -Compressed gas" },
        { key: "CW041", value: "GHS05 -Danger or Warning -Corrosive cat. 1" },
        { key: "CW042", value: "GHS06 - Danger -Toxic cat. 1 - 3" },
        { key: "CW043", value: "GHS07 - Warning -Toxic cat. 4, Irritant cat. 2 or 3, Lower systemic health hazards" },
        { key: "CW044", value: "GHS08 - Danger or Warning - Systemic health hazards" },
        { key: "CW045", value: "GHS09 - Warning (for cat. 1)- (for cat. 2 no signal word) Environment" },
        { key: "CW046", value: "H200 - Unstable explosives." },
        { key: "CW047", value: "H201 - Explosive; mass explosion hazard." },
        { key: "CW048", value: "H202 - Explosive, severe projection hazard." },
        { key: "CW049", value: "H203 - Explosive; fire, blast or projection hazard." },
        { key: "CW050", value: "H204 - Fire or projection hazard." },
        { key: "CW051", value: "H205 - May mass explode in fire." },
        { key: "CW052", value: "H220 - Extremely flammable gas." },
        { key: "CW053", value: "H221 - Flammable gas." },
        { key: "CW054", value: "H222 - Extremely flammable aerosol." },
        { key: "CW055", value: "H223 - Flammable aerosol." },
        { key: "CW056", value: "H224 - Extremely flammable liquid and vapour." },
        { key: "CW057", value: "H225 - Highly flammable liquid and vapour." },
        { key: "CW058", value: "H226 - Flammable liquid and vapour." },
        { key: "CW059", value: "H228 - Flammable solid." },
        { key: "CW060", value: "H240 - Heating may cause an explosion." },
        { key: "CW061", value: "H241 - Heating may cause a fire or explosion." },
        { key: "CW062", value: "H242 - Heating may cause a fire." },
        { key: "CW063", value: "H250 - Catches fire spontaneously if exposed to air." },
        { key: "CW064", value: "H251 - Self-heating: may catch fire." },
        { key: "CW065", value: "H252 - Self-heating in large quantities; may catch fire." },
        { key: "CW066", value: "H260 - In contact with water releases flammable gases which may ignite spontaneously." },
        { key: "CW067", value: "H261 - In contact with water releases flammable gases." },
        { key: "CW068", value: "H270 - May cause or intensify fire; oxidiser." },
        { key: "CW069", value: "H271 - May cause fire or explosion; strong oxidiser." },
        { key: "CW070", value: "H272 - May intensify fire; oxidiser." },
        { key: "CW071", value: "H280 - Contains gas under pressure; may explode if heated." },
        { key: "CW072", value: "H281 - Contains refrigerated gas; may cause cryogenic burns or injury." },
        { key: "CW073", value: "H290 - May be corrosive to metals." },
        { key: "CW074", value: "H300 - Fatal if swallowed." },
        { key: "CW075", value: "H301 - Toxic if swallowed." },
        { key: "CW076", value: "H302 - Harmful if swallowed." },
        { key: "CW077", value: "H304 - May be fatal if swallowed and enters airways." },
        { key: "CW078", value: "H310 - Fatal in contact with skin." },
        { key: "CW079", value: "H311 - Toxic in contact with skin." },
        { key: "CW080", value: "H312 - Harmful in contact with skin." },
        { key: "CW081", value: "H314 - Causes severe skin burns and eye damage." },
        { key: "CW082", value: "H315 - Causes skin irritation." },
        { key: "CW083", value: "H317 - May cause an allergic skin reaction." },
        { key: "CW084", value: "H318 - Causes serious eye damage." },
        { key: "CW085", value: "H319 - Causes serious eye irritation." },
        { key: "CW086", value: "H330 - Fatal if inhaled." },
        { key: "CW087", value: "H331 - Toxic if inhaled." },
        { key: "CW088", value: "H332 - Harmful if inhaled." },
        { key: "CW089", value: "H334 - May cause allergy or asthma symptoms or breathing difficulties if inhaled." },
        { key: "CW090", value: "H335 - May cause respiratory irritation." },
        { key: "CW091", value: "H336 - May cause drowsiness or dizziness." },
        { key: "CW092", value: "H340 - May cause genetic defects exposure cause the hazard>." },
        { key: "CW093", value: "H341 - Suspected of causing genetic defects." },
        { key: "CW094", value: "H350 - May cause cancer." },
        { key: "CW095", value: "H351 - Suspected of causing cancer." },
        { key: "CW096", value: "H360 - May damage fertility or the unborn child." },
        { key: "CW097", value: "H361 - Suspected of damaging fertility or the unborn child." },
        { key: "CW098", value: "H362 - May cause harm to breast-fed children." },
        { key: "CW099", value: "H370 - Causes damage to organs." },
        { key: "CW100", value: "H371 - May cause damage to organs." },
        { key: "CW101", value: "H372 - Causes damage to organs through prolonged or repeated exposure exposure cause the hazard>." },
        { key: "CW102", value: "H373 - May cause damage to organs through prolonged or repeated exposure." },
        { key: "CW103", value: "H400 - Very toxic to aquatic life." },
        { key: "CW104", value: "H410 - Very toxic to aquatic life with long lasting effects." },
        { key: "CW105", value: "H411 - Toxic to aquatic life with long lasting effects." },
        { key: "CW106", value: "H412 - Harmful to aquatic life with long lasting effects." },
        { key: "CW107", value: "H413 - May cause long lasting harmful effects to aquatic life." },
        { key: "CW108", value: "EUH 001 - Explosive when dry." },
        { key: "CW109", value: "EUH 006 - Explosive with or without contact with air." },
        { key: "CW110", value: "EUH 014 - Reacts violently with water." },
        { key: "CW111", value: "EUH 018 - In use, may form flammable/explosive vapour-air mixture." },
        { key: "CW112", value: "EUH 019 - May form explosive peroxides." },
        { key: "CW113", value: "EUH 044 - Risk of explosion if heated under confinement." },
        { key: "CW114", value: "EUH 029 - Contact with water liberates toxic gas." },
        { key: "CW115", value: "EUH 031 - Contact with acids liberates toxic gas." },
        { key: "CW116", value: "EUH 032 - Contact with acids liberates very toxic gas." },
        { key: "CW117", value: "EUH 066 - Repeated exposure may cause skin dryness or cracking." },
        { key: "CW118", value: "EUH 070 - Toxic by eye contact." },
        { key: "CW119", value: "EUH 071 - Corrosive to the respiratory tract." },
        { key: "CW120", value: "EUH 059 - Hazardous to the ozone layer." },
        { key: "CW121", value: "EUH 201 - Contains lead. Should not be used on surfaces liable to be chewed or sucked by children." },
        { key: "CW122", value: "EUH 201A - Warning! Contains lead." },
        { key: "CW123", value: "EUH 202 - Cyanoacrylate. Danger. Bonds skin and eyes in seconds. Keep out of the reach of children." },
        { key: "CW124", value: "EUH 203 - Contains chromium (VI). May produce an allergic reaction." },
        { key: "CW125", value: "EUH 204 - Contains isocyanates. May produce an allergic reaction." },
        { key: "CW126", value: "EUH 205 - Contains epoxy constituents. May produce an allergic reaction." },
        { key: "CW127", value: "EUH 206 - Warning! Do not use together with other products. May release dangerous gases (chlorine)" },
        { key: "CW128", value: "EUH 207 - Warning! Contains cadmium. Dangerous fumes are formed during use. See information supplied by the manufacturer. Comply with the safety instructions." },
        { key: "CW129", value: "EUH 208 - Contains (name of sensitising substance). May produce an allergic reaction." },
        { key: "CW130", value: "EUH 209 - Can become highly flammable in use." },
        { key: "CW131", value: "EUH 209A - Can become flammable in use." },
        { key: "CW132", value: "EUH 210 - Safety data sheet available on request." },
        { key: "CW133", value: "EUH 401 - To avoid risks to human health and the environment, comply with the instructions for use." },
        { key: "CW134", value: "P101 - If medical advice is needed, have product container or label at hand." },
        { key: "CW135", value: "P102 - Keep out of reach of children." },
        { key: "CW136", value: "P103 - Read label before use." },
        { key: "CW137", value: "P201 - Obtain special instructions before use." },
        { key: "CW138", value: "P202 - Do not handle until all safety precautions have been read and understood." },
        { key: "CW139", value: "P210 - Keep away from heat/sparks/open flames/hot surfaces. - No smoking." },
        { key: "CW140", value: "P211 - Do not spray on an open flame or other ignition source." },
        { key: "CW141", value: "P220 - Keep/Store away from clothing/.../combustible materials." },
        { key: "CW142", value: "P221 - Take any precaution to avoid mixing with combustibles..." },
        { key: "CW143", value: "P222 - Do not allow contact with air." },
        { key: "CW144", value: "P223 - Keep away from any possible contact with water, because of violent reaction and possible flash fire." },
        { key: "CW146", value: "P231 - Handle under inert gas." },
        { key: "CW147", value: "P232 - Protect from moisture." },
        { key: "CW148", value: "P233 - Keep container tightly closed." },
        { key: "CW149", value: "P234 - Keep only in original container." },
        { key: "CW150", value: "P235 - Keep cool." },
        { key: "CW151", value: "P240 - Ground/bond container and receiving equipment." },
        { key: "CW152", value: "P241 - Use explosion-proof electrical/ventilating/lighting/.../equipment." },
        { key: "CW153", value: "P242 - Use only non-sparking tools." },
        { key: "CW154", value: "P243 - Take precautionary measures against static discharge." },
        { key: "CW155", value: "P244 - Keep reduction valves free from grease and oil." },
        { key: "CW156", value: "P250 - Do not subject to grinding/shock/.../friction." },
        { key: "CW157", value: "P251 - Pressurized container: Do not pierce or burn, even after use." },
        { key: "CW158", value: "P260 - Do not breathe dust/fume/gas/mist/vapours/spray." },
        { key: "CW159", value: "P261 - Avoid breathing dust/fume/gas/mist/vapours/spray." },
        { key: "CW160", value: "P262 - Do not get in eyes, on skin, or on clothing." },
        { key: "CW161", value: "P263 - Avoid contact during pregnancy/while nursing." },
        { key: "CW162", value: "P264 - Wash thoroughly after handling." },
        { key: "CW163", value: "P270 - Do not eat, drink or smoke when using this product." },
        { key: "CW164", value: "P271 - Use only outdoors or in a well-ventilated area." },
        { key: "CW165", value: "P272 - Contaminated work clothing should not be allowed out of the workplace." },
        { key: "CW166", value: "P273 - Avoid release to the environment." },
        { key: "CW167", value: "P280 - Wear protective gloves/protective clothing/eye protection/face protection." },
        { key: "CW168", value: "P281 - Use personal protective equipment as required." },
        { key: "CW169", value: "P282 - Wear cold insulating gloves/face shield/eye protection." },
        { key: "CW170", value: "P283 - Wear fire/flame resistant/retardant clothing." },
        { key: "CW171", value: "P284 - Wear respiratory protection." },
        { key: "CW172", value: "P285 - In case of inadequate ventilation wear respiratory protection." },
        { key: "CW173", value: "P231 + P232 - Handle under inert gas. Protect from moisture." },
        { key: "CW175", value: "P301 - IF SWALLOWED: P310 - Immediately call a POISON CENTER or doctor/physician." },
        { key: "CW176", value: "P302 - IF ON SKIN: P310 - Immediately call a POISON CENTER or doctor/physician." },
        { key: "CW177", value: "P303 - IF ON SKIN (or hair): P310 - Immediately call a POISON CENTER or doctor/physician." },
        { key: "CW178", value: "P304 - IF INHALED: P310 - Immediately call a POISON CENTER or doctor/physician." },
        { key: "CW179", value: "P305 - IF IN EYES: P310 - Immediately call a POISON CENTER or doctor/physician." },
        { key: "CW180", value: "P306 - IF ON CLOTHING: P312 - Call a POISON CENTER or doctor/physician if you feel unwell." },
        { key: "CW181", value: "P307 - IF exposed: P310 - Immediately call a POISON CENTER or doctor/physician." },
        { key: "CW182", value: "P308 - IF exposed or concerned: P310 - Immediately call a POISON CENTER or doctor/physician." },
        { key: "CW183", value: "P309 - IF exposed or if you feel unwell: P310 - Immediately call a POISON CENTER or doctor/physician." },
        { key: "CW184", value: "P342 - If experiencing respiratory symptoms: P310 - Immediately call a POISON CENTER or doctor/physician." },
        { key: "CW185", value: "P342 - If experiencing respiratory symptoms: P311 - Call a POISON CENTER or doctor/physician." },
        { key: "CW186", value: "P342 - If experiencing respiratory symptoms: P312 - Call a POISON CENTER or doctor/physician if you feel unwell." },
        { key: "CW187", value: "P342 - If experiencing respiratory symptoms: P313 - Get medical advice/attention." },
        { key: "CW188", value: "P342 - If experiencing respiratory symptoms: P314 - Get medical advice/attention if you feel unwell." },
        { key: "CW189", value: "P342 - If experiencing respiratory symptoms: P315 - Get immediate medical advice/attention." },
        { key: "CW190", value: "P342 - If experiencing respiratory symptoms: P320 - Specific treatment is urgent (see on this label)." },
        { key: "CW191", value: "P342 - If experiencing respiratory symptoms: P321 - Specific treatment (see on this label)." },
        { key: "CW192", value: "P342 - If experiencing respiratory symptoms: P322 - Specific measures (see on this label)." },
        { key: "CW193", value: "P301 - IF SWALLOWED: P330 - Rinse mouth." },
        { key: "CW194", value: "P301 - IF SWALLOWED: P331 - Do NOT induce vomiting." },
        { key: "CW195", value: "P332 - If skin irritation occurs: P322 - Specific measures (see on this label)." },
        { key: "CW196", value: "P333 - If skin irritation or rash occurs: P322 - Specific measures (see on this label)." },
        { key: "CW197", value: "P332 - If skin irritation occurs: P334 - Immerse in cool water/wrap in wet bandages." },
        { key: "CW198", value: "P332 - If skin irritation occurs: P335 - Brush off loose particles from skin." },
        { key: "CW199", value: "P332 - If skin irritation occurs: P336 - Thaw frosted parts with lukewarm water. Do no rub affected area." },
        { key: "CW201", value: "P337 - If eye irritation persists: P338 - Remove contact lenses, if present and easy to do. Continue rinsing." },
        { key: "CW202", value: "P342 - If experiencing respiratory symptoms: P340 - Remove victim to fresh air and keep at rest in a position comfortable for breathing." },
        { key: "CW203", value: "P341 - If breathing is difficult, remove victim to fresh air and keep at rest in a position comfortable for breathing." },
        { key: "CW206", value: "P333 - If skin irritation or rash occurs: P350 - Gently wash with plenty of soap and water." },
        { key: "CW207", value: "P337 - If eye irritation persists: P351 - Rinse cautiously with water for several minutes." },
        { key: "CW208", value: "P333 - If skin irritation or rash occurs: P352 - Wash with plenty of soap and water." },
        { key: "CW209", value: "P333 - If skin irritation or rash occurs: P353 - Rinse skin with water/shower." },
        { key: "CW210", value: "P333 - If skin irritation or rash occurs: P360 - Rinse immediately contaminated clothing and skin with plenty of water before removing clothes." },
        { key: "CW211", value: "P333 - If skin irritation or rash occurs: P361 - Remove/Take off immediately all contaminated clothing." },
        { key: "CW212", value: "P333 - If skin irritation or rash occurs: P362 - Take off contaminated clothing and wash before reuse." },
        { key: "CW213", value: "P333 - If skin irritation or rash occurs: P363 - Wash contaminated clothing before reuse." },
        { key: "CW216", value: "P372 - Explosion risk in case of fire." },
        { key: "CW217", value: "P373 - DO NOT fight fire when fire reaches explosives." },
        { key: "CW218", value: "P370 - In case of fire: P374 - Fight fire with normal precautions from a reasonable distance." },
        { key: "CW219", value: "P370 - In case of fire: P375 - Fight fire remotely due to the risk of explosion." },
        { key: "CW220", value: "P376 - Stop leak if safe to do so." },
        { key: "CW221", value: "P377 - Leaking gas fire: Do not extinguish, unless leak can be stopped safely." },
        { key: "CW223", value: "P370 - In case of fire: P380 - Evacuate area." },
        { key: "CW224", value: "P370 - In case of fire: P381 - Eliminate all ignition sources if safe to do so." },
        { key: "CW225", value: "P390 - Absorb spillage to prevent material damage." },
        { key: "CW226", value: "P391 - Collect spillage." },
        { key: "CW254", value: "Do not Use Tap Water" },
        { key: "CW255", value: "Do Not use saliva" },
        { key: "CW257", value: "Warning; Crushing of hands" },
        { key: "CW258", value: "Warning; Hot surface" },
        { key: "CW259", value: "Warning; Electricity" },
        { key: "CW260", value: "Warning; Counterrotating rollers" },
        { key: "CW261", value: "Warning; Optical radiation" },
        { key: "CW262", value: "Single patient multiple use" },
        { key: "CW263", value: "Contains human blood or plasma derivatives" },
        { key: "CW264", value: "Contains a medicinal substance" },
        { key: "CW265", value: "Contains hazardous substances" },
        { key: "CW266", value: "Contains biological material of human origin" },
        { key: "CW267", value: "Contains biological material of animal origin" },
        { key: "CW268", value: "H229 Pressurized container: may burst if heated" },
        { key: "CW269", value: "H230 May react explosively even in the absence of air" },
        { key: "CW270", value: "H231 May react explosively even in the absence of air at elevated pressure and/or temperature" },
        { key: "CW271", value: "H420 Harms public health and the environment by destroying ozone in the upper atmosphere" },
        { key: "CW272", value: "P364 And wash it before reuse." },
        { key: "CW273", value: "Caution, risk of electric shock" },
        { key: "CW274", value: "Non-ionizing electromagnetic radiation" },
        { key: "CW275", value: "Ask your pharmacist or doctor for advice before using or taking the product (e.g. if pregnant or breastfeeding)" },
        { key: "CW276", value: "Potential Interactions with other products (e.g. medicines, food)" },
        { key: "CW277", value: "If any symptoms are troublesome or last more than a few days or if you notice any side effects not mentioned, please inform your doctor or pharmacist" },
        { key: "CW278", value: "Avoid contact with non-sterile water" },
        { key: "CW279", value: "P337 - If eye irritation persists: P310 - Immediately call a POISON CENTER or doctor/physician." },
        { key: "CW280", value: "P332 - If skin irritation occurs: P310 - Immediately call a POISON CENTER or doctor/physician." },
        { key: "CW281", value: "P333 - If skin irritation or rash occurs: P310 - Immediately call a POISON CENTER or doctor/physician." },
        { key: "CW282", value: "P337 - If eye irritation persists: P311 - Call a POISON CENTER or doctor/physician." },
        { key: "CW283", value: "P332 - If skin irritation occurs: P311 - Call a POISON CENTER or doctor/physician." },
        { key: "CW284", value: "P333 - If skin irritation or rash occurs: P311 - Call a POISON CENTER or doctor/physician." },
        { key: "CW285", value: "P301 - IF SWALLOWED: P311 - Call a POISON CENTER or doctor/physician." },
        { key: "CW286", value: "P302 - IF ON SKIN: P311 - Call a POISON CENTER or doctor/physician." },
        { key: "CW287", value: "P303 - IF ON SKIN (or hair): P311 - Call a POISON CENTER or doctor/physician." },
        { key: "CW288", value: "P304 - IF INHALED: P311 - Call a POISON CENTER or doctor/physician." },
        { key: "CW289", value: "P305 - IF IN EYES: P311 - Call a POISON CENTER or doctor/physician." },
        { key: "CW290", value: "P307 - IF exposed: P311 - Call a POISON CENTER or doctor/physician." },
        { key: "CW291", value: "P308 - IF exposed or concerned: P311 - Call a POISON CENTER or doctor/physician." },
        { key: "CW292", value: "P309 - IF exposed or if you feel unwell: P311 - Call a POISON CENTER or doctor/physician." },
        { key: "CW293", value: "P337 - If eye irritation persists: P312 - Call a POISON CENTER or doctor/physician if you feel unwell." },
        { key: "CW294", value: "P332 - If skin irritation occurs: P312 - Call a POISON CENTER or doctor/physician if you feel unwell." },
        { key: "CW295", value: "P333 - If skin irritation or rash occurs: P312 - Call a POISON CENTER or doctor/physician if you feel unwell." },
        { key: "CW296", value: "P301 - IF SWALLOWED: P312 - Call a POISON CENTER or doctor/physician if you feel unwell." },
        { key: "CW297", value: "P302 - IF ON SKIN: P312 - Call a POISON CENTER or doctor/physician if you feel unwell." },
        { key: "CW298", value: "P303 - IF ON SKIN (or hair): P312 - Call a POISON CENTER or doctor/physician if you feel unwell." },
        { key: "CW299", value: "P304 - IF INHALED: P312 - Call a POISON CENTER or doctor/physician if you feel unwell." },
        { key: "CW300", value: "P305 - IF IN EYES: P312 - Call a POISON CENTER or doctor/physician if you feel unwell." },
        { key: "CW301", value: "P307 - IF exposed: P312 - Call a POISON CENTER or doctor/physician if you feel unwell." },
        { key: "CW302", value: "P308 - IF exposed or concerned: P312 - Call a POISON CENTER or doctor/physician if you feel unwell." },
        { key: "CW303", value: "P309 - IF exposed or if you feel unwell: P312 - Call a POISON CENTER or doctor/physician if you feel unwell." },
        { key: "CW304", value: "P337 - If eye irritation persists: P313 - Get medical advice/attention." },
        { key: "CW305", value: "P332 - If skin irritation occurs: P313 - Get medical advice/attention." },
        { key: "CW306", value: "P333 - If skin irritation or rash occurs: P313 - Get medical advice/attention." },
        { key: "CW307", value: "P301 - IF SWALLOWED: P313 - Get medical advice/attention." },
        { key: "CW308", value: "P302 - IF ON SKIN: P313 - Get medical advice/attention." },
        { key: "CW309", value: "P303 - IF ON SKIN (or hair): P313 - Get medical advice/attention." },
        { key: "CW310", value: "P304 - IF INHALED: P313 - Get medical advice/attention." },
        { key: "CW311", value: "P305 - IF IN EYES: P313 - Get medical advice/attention." },
        { key: "CW312", value: "P307 - IF exposed: P313 - Get medical advice/attention." },
        { key: "CW313", value: "P308 - IF exposed or concerned: P313 - Get medical advice/attention." },
        { key: "CW314", value: "P309 - IF exposed or if you feel unwell: P313 - Get medical advice/attention." },
        { key: "CW315", value: "P337 - If eye irritation persists: P314 - Get medical advice/attention if you feel unwell." },
        { key: "CW316", value: "P332 - If skin irritation occurs: P314 - Get medical advice/attention if you feel unwell." },
        { key: "CW317", value: "P333 - If skin irritation or rash occurs: P314 - Get medical advice/attention if you feel unwell." },
        { key: "CW318", value: "P301 - IF SWALLOWED: P314 - Get medical advice/attention if you feel unwell." },
        { key: "CW319", value: "P302 - IF ON SKIN: P314 - Get medical advice/attention if you feel unwell." },
        { key: "CW320", value: "P303 - IF ON SKIN (or hair): P314 - Get medical advice/attention if you feel unwell." },
        { key: "CW321", value: "P304 - IF INHALED: P314 - Get medical advice/attention if you feel unwell." },
        { key: "CW322", value: "P305 - IF IN EYES: P314 - Get medical advice/attention if you feel unwell." },
        { key: "CW323", value: "P306 - IF ON CLOTHING: P314 - Get medical advice/attention if you feel unwell." },
        { key: "CW324", value: "P307 - IF exposed: P314 - Get medical advice/attention if you feel unwell." },
        { key: "CW325", value: "P308 - IF exposed or concerned: P314 - Get medical advice/attention if you feel unwell." },
        { key: "CW326", value: "P309 - IF exposed or if you feel unwell: P314 - Get medical advice/attention if you feel unwell." },
        { key: "CW327", value: "P337 - If eye irritation persists: P315 - Get immediate medical advice/attention." },
        { key: "CW328", value: "P332 - If skin irritation occurs: P315 - Get immediate medical advice/attention." },
        { key: "CW329", value: "P333 - If skin irritation or rash occurs: P315 - Get immediate medical advice/attention." },
        { key: "CW330", value: "P301 - IF SWALLOWED: P315 - Get immediate medical advice/attention." },
        { key: "CW331", value: "P302 - IF ON SKIN: P315 - Get immediate medical advice/attention." },
        { key: "CW332", value: "P303 - IF ON SKIN (or hair): P315 - Get immediate medical advice/attention." },
        { key: "CW333", value: "P304 - IF INHALED: P315 - Get immediate medical advice/attention." },
        { key: "CW334", value: "P305 - IF IN EYES: P315 - Get immediate medical advice/attention." },
        { key: "CW335", value: "P307 - IF exposed: P315 - Get immediate medical advice/attention." },
        { key: "CW336", value: "P308 - IF exposed or concerned: P315 - Get immediate medical advice/attention." },
        { key: "CW337", value: "P309 - IF exposed or if you feel unwell: P315 - Get immediate medical advice/attention." },
        { key: "CW338", value: "P337 - If eye irritation persists: P320 - Specific treatment is urgent (see on this label)." },
        { key: "CW339", value: "P332 - If skin irritation occurs: P320 - Specific treatment is urgent (see on this label)." },
        { key: "CW340", value: "P333 - If skin irritation or rash occurs: P320 - Specific treatment is urgent (see on this label)." },
        { key: "CW341", value: "P301 - IF SWALLOWED: P320 - Specific treatment is urgent (see on this label)." },
        { key: "CW342", value: "P302 - IF ON SKIN: P320 - Specific treatment is urgent (see on this label)." },
        { key: "CW343", value: "P303 - IF ON SKIN (or hair): P320 - Specific treatment is urgent (see on this label)." },
        { key: "CW344", value: "P304 - IF INHALED: P320 - Specific treatment is urgent (see on this label)." },
        { key: "CW345", value: "P305 - IF IN EYES: P320 - Specific treatment is urgent (see on this label)." },
        { key: "CW346", value: "P306 - IF ON CLOTHING: P320 - Specific treatment is urgent (see on this label)." },
        { key: "CW347", value: "P307 - IF exposed: P320 - Specific treatment is urgent (see on this label)." },
        { key: "CW348", value: "P308 - IF exposed or concerned: P320 - Specific treatment is urgent (see on this label)." },
        { key: "CW349", value: "P309 - IF exposed or if you feel unwell: P320 - Specific treatment is urgent (see on this label)." },
        { key: "CW350", value: "P337 - If eye irritation persists: P321 - Specific treatment (see on this label)." },
        { key: "CW351", value: "P332 - If skin irritation occurs: P321 - Specific treatment (see on this label)." },
        { key: "CW352", value: "P333 - If skin irritation or rash occurs: P321 - Specific treatment (see on this label)." },
        { key: "CW353", value: "P301 - IF SWALLOWED: P321 - Specific treatment (see on this label)." },
        { key: "CW354", value: "P302 - IF ON SKIN: P321 - Specific treatment (see on this label)." },
        { key: "CW355", value: "P303 - IF ON SKIN (or hair): P321 - Specific treatment (see on this label)." },
        { key: "CW356", value: "P304 - IF INHALED: P321 - Specific treatment (see on this label)." },
        { key: "CW357", value: "P305 - IF IN EYES: P321 - Specific treatment (see on this label)." },
        { key: "CW358", value: "P306 - IF ON CLOTHING: P321 - Specific treatment (see on this label)." },
        { key: "CW359", value: "P307 - IF exposed: P321 - Specific treatment (see on this label)." },
        { key: "CW360", value: "P308 - IF exposed or concerned: P321 - Specific treatment (see on this label)." },
        { key: "CW361", value: "P309 - IF exposed or if you feel unwell: P321 - Specific treatment (see on this label)." },
        { key: "CW362", value: "P337 - If eye irritation persists: P322 - Specific measures (see on this label)." },
        { key: "CW363", value: "P301 - IF SWALLOWED: P322 - Specific measures (see on this label)." },
        { key: "CW364", value: "P302 - IF ON SKIN: P322 - Specific measures (see on this label)." },
        { key: "CW365", value: "P303 - IF ON SKIN (or hair): P322 - Specific measures (see on this label)." },
        { key: "CW366", value: "P304 - IF INHALED: P322 - Specific measures (see on this label)." },
        { key: "CW367", value: "P305 - IF IN EYES: P322 - Specific measures (see on this label)." },
        { key: "CW368", value: "P306 - IF ON CLOTHING: P322 - Specific measures (see on this label)." },
        { key: "CW369", value: "P307 - IF exposed: P322 - Specific measures (see on this label)." },
        { key: "CW370", value: "P308 - IF exposed or concerned: P322 - Specific measures (see on this label)." },
        { key: "CW371", value: "P309 - IF exposed or if you feel unwell: P322 - Specific measures (see on this label)." },
        { key: "CW372", value: "P307 - IF exposed: P330 - Rinse mouth." },
        { key: "CW373", value: "P308 - IF exposed or concerned: P330 - Rinse mouth." },
        { key: "CW374", value: "P309 - IF exposed or if you feel unwell: P330 - Rinse mouth." },
        { key: "CW375", value: "P307 - IF exposed: P331 - Do NOT induce vomiting." },
        { key: "CW376", value: "P308 - IF exposed or concerned: P331 - Do NOT induce vomiting." },
        { key: "CW377", value: "P309 - IF exposed or if you feel unwell: P331 - Do NOT induce vomiting." },
        { key: "CW378", value: "P333 - If skin irritation occurs or rash occurs: P334 - Immerse in cool water/wrap in wet bandages." },
        { key: "CW379", value: "P302 - IF ON SKIN: P334 - Immerse in cool water/wrap in wet bandages." },
        { key: "CW380", value: "P303 - IF ON SKIN (or hair): P334 - Immerse in cool water/wrap in wet bandages." },
        { key: "CW381", value: "P337 - If eye irritation persists: P334 - Immerse in cool water/wrap in wet bandages." },
        { key: "CW382", value: "P305 - IF IN EYES: P334 - Immerse in cool water/wrap in wet bandages." },
        { key: "CW383", value: "P333 - If skin irritation occurs or rash occurs: P335 - Brush off loose particles from skin." },
        { key: "CW384", value: "P302 - IF ON SKIN: P335 - Brush off loose particles from skin." },
        { key: "CW385", value: "P303 - IF ON SKIN (or hair): P335 - Brush off loose particles from skin." },
        { key: "CW386", value: "P306 - IF ON CLOTHING: P335 - Brush off loose particles from skin." },
        { key: "CW387", value: "P307 - IF exposed: P335 - Brush off loose particles from skin." },
        { key: "CW388", value: "P308 - IF exposed or concerned: P335 - Brush off loose particles from skin." },
        { key: "CW389", value: "P309 - IF exposed or if you feel unwell: P335 - Brush off loose particles from skin." },
        { key: "CW390", value: "P333 - If skin irritation occurs or rash occurs: P336 - Thaw frosted parts with lukewarm water. Do no rub affected area." },
        { key: "CW391", value: "P302 - IF ON SKIN: P336 - Thaw frosted parts with lukewarm water. Do no rub affected area." },
        { key: "CW392", value: "P303 - IF ON SKIN (or hair): P336 - Thaw frosted parts with lukewarm water. Do no rub affected area." },
        { key: "CW393", value: "P305 - IF IN EYES: P336 - Thaw frosted parts with lukewarm water. Do no rub affected area." },
        { key: "CW394", value: "P307 - IF exposed: P336 - Thaw frosted parts with lukewarm water. Do no rub affected area." },
        { key: "CW395", value: "P308 - IF exposed or concerned: P336 - Thaw frosted parts with lukewarm water. Do no rub affected area." },
        { key: "CW396", value: "P309 - IF exposed or if you feel unwell: P336 - Thaw frosted parts with lukewarm water. Do no rub affected area." },
        { key: "CW397", value: "P305 - IF IN EYES: P338 - Remove contact lenses, if present and easy to do. Continue rinsing." },
        { key: "CW398", value: "P307 - IF exposed: P338 - Remove contact lenses, if present and easy to do. Continue rinsing." },
        { key: "CW399", value: "P308 - IF exposed or concerned: P338 - Remove contact lenses, if present and easy to do. Continue rinsing." },
        { key: "CW400", value: "P309 - IF exposed or if you feel unwell: P338 - Remove contact lenses, if present and easy to do. Continue rinsing." },
        { key: "CW401", value: "P304 - IF INHALED: P340 - Remove victim to fresh air and keep at rest in a position comfortable for breathing." },
        { key: "CW402", value: "P307 - IF exposed: P340 - Remove victim to fresh air and keep at rest in a position comfortable for breathing." },
        { key: "CW403", value: "P308 - IF exposed or concerned: P340 - Remove victim to fresh air and keep at rest in a position comfortable for breathing." },
        { key: "CW404", value: "P309 - IF exposed or if you feel unwell: P340 - Remove victim to fresh air and keep at rest in a position comfortable for breathing." },
        { key: "CW405", value: "P332 - If skin irritation occurs: P350 - Gently wash with plenty of soap and water." },
        { key: "CW406", value: "P302 - IF ON SKIN: P350 - Gently wash with plenty of soap and water." },
        { key: "CW407", value: "P303 - IF ON SKIN (or hair): P350 - Gently wash with plenty of soap and water." },
        { key: "CW408", value: "P306 - IF ON CLOTHING: P350 - Gently wash with plenty of soap and water." },
        { key: "CW409", value: "P307 - IF exposed: P350 - Gently wash with plenty of soap and water." },
        { key: "CW410", value: "P308 - IF exposed or concerned: P350 - Gently wash with plenty of soap and water." },
        { key: "CW411", value: "P309 - IF exposed or if you feel unwell: P350 - Gently wash with plenty of soap and water." },
        { key: "CW412", value: "P333 - If skin irritation or rash occurs: P351 - Rinse cautiously with water for several minutes." },
        { key: "CW413", value: "P332 - If skin irritation occurs: P351 - Rinse cautiously with water for several minutes." },
        { key: "CW414", value: "P302 - IF ON SKIN: P351 - Rinse cautiously with water for several minutes." },
        { key: "CW415", value: "P303 - IF ON SKIN (or hair): P351 - Rinse cautiously with water for several minutes." },
        { key: "CW416", value: "P305 - IF IN EYES: P351 - Rinse cautiously with water for several minutes." },
        { key: "CW417", value: "P306 - IF ON CLOTHING: P351 - Rinse cautiously with water for several minutes." },
        { key: "CW418", value: "P307 - IF exposed: P351 - Rinse cautiously with water for several minutes." },
        { key: "CW419", value: "P308 - IF exposed or concerned: P351 - Rinse cautiously with water for several minutes." },
        { key: "CW420", value: "P309 - IF exposed or if you feel unwell: P351 - Rinse cautiously with water for several minutes." },
        { key: "CW421", value: "P332 - If skin irritation occurs: P352 - Wash with plenty of soap and water." },
        { key: "CW422", value: "P302 - IF ON SKIN: P352 - Wash with plenty of soap and water." },
        { key: "CW423", value: "P303 - IF ON SKIN (or hair): P352 - Wash with plenty of soap and water." },
        { key: "CW424", value: "P306 - IF ON CLOTHING: P352 - Wash with plenty of soap and water." },
        { key: "CW425", value: "P307 - IF exposed: P352 - Wash with plenty of soap and water." },
        { key: "CW426", value: "P308 - IF exposed or concerned: P352 - Wash with plenty of soap and water." },
        { key: "CW427", value: "P309 - IF exposed or if you feel unwell: P352 - Wash with plenty of soap and water." },
        { key: "CW428", value: "P332 - If skin irritation occurs: P353 - Rinse skin with water/shower." },
        { key: "CW429", value: "P302 - IF ON SKIN: P353 - Rinse skin with water/shower." },
        { key: "CW430", value: "P303 - IF ON SKIN (or hair): P353 - Rinse skin with water/shower." },
        { key: "CW431", value: "P306 - IF ON CLOTHING: P353 - Rinse skin with water/shower." },
        { key: "CW432", value: "P307 - IF exposed: P353 - Rinse skin with water/shower." },
        { key: "CW433", value: "P308 - IF exposed or concerned: P353 - Rinse skin with water/shower." },
        { key: "CW434", value: "P309 - IF exposed or if you feel unwell: P353 - Rinse skin with water/shower." },
        { key: "CW435", value: "P332 - If skin irritation occurs: P360 - Rinse immediately contaminated clothing and skin with plenty of water before removing clothes." },
        { key: "CW436", value: "P302 - IF ON SKIN: P360 - Rinse immediately contaminated clothing and skin with plenty of water before removing clothes." },
        { key: "CW437", value: "P303 - IF ON SKIN (or hair): P360 - Rinse immediately contaminated clothing and skin with plenty of water before removing clothes." },
        { key: "CW438", value: "P306 - IF ON CLOTHING: P360 - Rinse immediately contaminated clothing and skin with plenty of water before removing clothes." },
        { key: "CW439", value: "P307 - IF exposed: P360 - Rinse immediately contaminated clothing and skin with plenty of water before removing clothes." },
        { key: "CW440", value: "P308 - IF exposed or concerned: P360 - Rinse immediately contaminated clothing and skin with plenty of water before removing clothes." },
        { key: "CW441", value: "P309 - IF exposed or if you feel unwell: P360 - Rinse immediately contaminated clothing and skin with plenty of water before removing clothes." },
        { key: "CW442", value: "P332 - If skin irritation occurs: P361 - Remove/Take off immediately all contaminated clothing." },
        { key: "CW443", value: "P302 - IF ON SKIN: P361 - Remove/Take off immediately all contaminated clothing." },
        { key: "CW444", value: "P303 - IF ON SKIN (or hair): P361 - Remove/Take off immediately all contaminated clothing." },
        { key: "CW445", value: "P306 - IF ON CLOTHING: P361 - Remove/Take off immediately all contaminated clothing." },
        { key: "CW446", value: "P307 - IF exposed: P361 - Remove/Take off immediately all contaminated clothing." },
        { key: "CW447", value: "P308 - IF exposed or concerned: P361 - Remove/Take off immediately all contaminated clothing." },
        { key: "CW448", value: "P309 - IF exposed or if you feel unwell: P361 - Remove/Take off immediately all contaminated clothing." },
        { key: "CW449", value: "P332 - If skin irritation occurs: P362 - Take off contaminated clothing and wash before reuse." },
        { key: "CW450", value: "P302 - IF ON SKIN: P362 - Take off contaminated clothing and wash before reuse." },
        { key: "CW451", value: "P303 - IF ON SKIN (or hair): P362 - Take off contaminated clothing and wash before reuse." },
        { key: "CW452", value: "P306 - IF ON CLOTHING: P362 - Take off contaminated clothing and wash before reuse." },
        { key: "CW453", value: "P307 - IF exposed: P362 - Take off contaminated clothing and wash before reuse." },
        { key: "CW454", value: "P308 - IF exposed or concerned: P362 - Take off contaminated clothing and wash before reuse." },
        { key: "CW455", value: "P309 - IF exposed or if you feel unwell: P362 - Take off contaminated clothing and wash before reuse." },
        { key: "CW456", value: "P332 - If skin irritation occurs: P363 - Wash contaminated clothing before reuse." },
        { key: "CW457", value: "P302 - IF ON SKIN: P363 - Wash contaminated clothing before reuse." },
        { key: "CW458", value: "P303 - IF ON SKIN (or hair): P363 - Wash contaminated clothing before reuse." },
        { key: "CW459", value: "P306 - IF ON CLOTHING: P363 - Wash contaminated clothing before reuse." },
        { key: "CW460", value: "P307 - IF exposed: P363 - Wash contaminated clothing before reuse." },
        { key: "CW461", value: "P308 - IF exposed or concerned: P363 - Wash contaminated clothing before reuse." },
        { key: "CW462", value: "P309 - IF exposed or if you feel unwell: P363 - Wash contaminated clothing before reuse." },
        { key: "CW463", value: "P371 - In case of major fire and large quantities: P374 - Fight fire with normal precautions from a reasonable distance." },
        { key: "CW464", value: "P371 - In case of major fire and large quantities: P375 - Fight fire remotely due to the risk of explosion." },
        { key: "CW465", value: "P371 - In case of major fire and large quantities: P380 - Evacuate area." },
        { key: "CW466", value: "P371 - In case of major fire and large quantities: P381 - Eliminate all ignition sources if safe to do so." },
        { key: "CW999", value: "OTHER **" }
    ];

    const languages = [
        { key: "en", value: "English" }, { key: "is", value: "Icelandic" },
        { key: "no", value: "Norwegian" }, { key: "tr", value: "Turkish" },
        { key: "bg", value: "Bulgarian" }, { key: "cs", value: "Czech" },
        { key: "da", value: "Danish" }, { key: "de", value: "German" },
        { key: "et", value: "Estonian" }, { key: "el", value: "Greek" },
        { key: "es", value: "Spanish" }, { key: "fr", value: "French" },
        { key: "hr", value: "Croatian" }, { key: "it", value: "Italian" },
        { key: "lv", value: "Latvian" }, { key: "lt", value: "Lithuanian" },
        { key: "hu", value: "Hungarian" }, { key: "mt", value: "Maltese" },
        { key: "nl", value: "Dutch" }, { key: "pl", value: "Polish" },
        { key: "pt", value: "Portuguese" }, { key: "ro", value: "Romanian" },
        { key: "sk", value: "Slovak" }, { key: "sl", value: "Slovenian" },
        { key: "fi", value: "Finnish" }, { key: "sv", value: "Swedish" },
        { key: "ga", value: "Irish" }
    ];

    // --- Main State ---
    const [items, setItems] = useState(() => {
        try {
            return (typeof initialData === 'string' ? JSON.parse(initialData) : initialData) || [];
        } catch {
            return [];
        }
    });


    const [isEditing, setIsEditing] = useState(forceIsEditing != null ? forceIsEditing : false);
    const [editingIndex, setEditingIndex] = useState(null);

    // --- Form State: Main Item ---
    const initialTypeState = { Value: cwctype[0].key, Text: cwctype[0].value };
    const [currentType, setCurrentType] = useState(initialTypeState);
    const [currentDescriptions, setCurrentDescriptions] = useState([]);

    // --- Form State: Sub-Item (Descriptions) ---
    const initialDescLangState = { Value: 'en', Text: 'English' };
    const [descLanguage, setDescLanguage] = useState(initialDescLangState);
    const [descText, setDescText] = useState('');

    const currentTypeError = currentType.Value === CRITICAL_WARNING_OTHER ? otherWarningError(currentDescriptions) : null;
    const invalidItems = findInvalidOtherWarnings(items);

    // --- Handlers: Sub-Item (Descriptions) ---
    const handleAddDescription = () => {
        if (!descText.trim()) return;

        const newDesc = {
            Description: {
                Language: descLanguage,
                Value: descText
            }
        };

        setCurrentDescriptions([...currentDescriptions, newDesc]);
        setDescText(''); // Reset text box, keep language
    };

    const handleRemoveDescription = (index) => {
        setCurrentDescriptions(currentDescriptions.filter((_, i) => i !== index));
    };

    const handleDescLanguageChange = (e) => {
        const value = e.target.value;
        const text = e.target.options[e.target.selectedIndex].text;
        setDescLanguage({ Value: value, Text: text });
    };

    // --- Handlers: Main Item ---
    const handleTypeChange = (e) => {
        const value = e.target.value;
        const text = e.target.options[e.target.selectedIndex].text;
        setCurrentType({ Value: value, Text: text });
    };

    const handleSaveItem = () => {
        if (currentTypeError) return;

        const newItem = {
            Type: currentType,
            Descriptions: currentDescriptions
        };

        const updatedItems = [...items];
        if (editingIndex !== null) {
            updatedItems[editingIndex] = newItem;
        } else {
            updatedItems.push(newItem);
        }

        updateGlobalState(updatedItems);
        popup.close();
    };

    const handleRemoveItem = (index) => {
        const updatedItems = items.filter((_, i) => i !== index);
        updateGlobalState(updatedItems);
        resetMainForm();
    };

    const handleEditItem = (index) => {
        const itemToEdit = items[index];
        setCurrentType(itemToEdit.Type || initialTypeState);
        // Deep copy descriptions to prevent direct reference mutation during edits
        setCurrentDescriptions(JSON.parse(JSON.stringify(itemToEdit.Descriptions || [])));
        setEditingIndex(index);
        setDescText(''); // Reset sub-form input
        popup.openEdit();
    };

    const resetMainForm = () => {
        setCurrentType(initialTypeState);
        setCurrentDescriptions([]);
        setEditingIndex(null);
        setDescText('');
    };

    const popup = useUDIRowPopup(resetMainForm);

    const updateGlobalState = (updatedItems) => {
        setItems(updatedItems);
        setUDI(prevItem => ({
            ...prevItem,
            ListOfCriticalWarningsOrContraindications: updatedItems
        }));
    };



    const changeIsEditingField = (state) => {

        if (forceIsEditing != null)
            return;

        setIsEditing(state);

    }


    useEffect(() => {

        if (forceIsEditing != null)
            setIsEditing(forceIsEditing);

    }, [forceIsEditing]);

    // Parent's Save failed on this field - open the first invalid CW999 so its error shows
    useEffect(() => {
        if (!validationTrigger || invalidItems.length === 0)
            return;
        const index = items.indexOf(invalidItems[0]);
        if (editingIndex !== index)
            handleEditItem(index);
    }, [validationTrigger]);


    // --- Render Mode: Edit ---
    return (


        <div className="view-task-field">
            <div className="view-task-field-line">
                <div className="view-task-field-title">{fieldTitle}</div>
                <div className="view-task-field-value">

                    <div style={{ backgroundColor: '#f8f8f8', padding: '10px' }}>

                        {isEditing ?


                            <div style={{ backgroundColor: '#f8f8f8', padding: '10px' }}>

                                {/* 1. Main Data Table */}
                                <table className="table table-bordered">
                                    <thead>
                                        <tr>
                                            <th>Type</th>
                                            <th>Descriptions</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {items.map((item, index) => (
                                            <tr
                                                key={index}
                                                className={editingIndex === index ? 'udi-complex-field-row-selected' : ''}
                                            >
                                                <td>{item.Type?.Text}</td>
                                                <td>
                                                    <ul style={{ margin: 0, paddingLeft: '20px' }}>
                                                        {(item.Descriptions || []).map((descItem, dIndex) => {
                                                            const lang = descItem.Description?.Language?.Text || '';
                                                            const text = descItem.Description?.Value || '';
                                                            return (
                                                                <li key={dIndex} style={{ whiteSpace: 'pre-wrap' }}>
                                                                    {lang ? `${lang}: ${text}` : text}
                                                                </li>
                                                            );
                                                        })}
                                                    </ul>
                                                </td>
                                                <td>
                                                    <button type="button" className="udiEditButton" onClick={() => handleEditItem(index)}>
                                                        <i className="fas fa-edit"></i>
                                                    </button>
                                                    <button type="button" className="udiRemoveButton" onClick={() => handleRemoveItem(index)}>
                                                        <i className="fas fa-times-circle"></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>

                                {invalidItems.length > 0 && (
                                    <div><span className="form-field-error-message">
                                        {otherWarningError(invalidItems[0].Descriptions)}
                                    </span></div>
                                )}

                                <UDIAddRowButton label="Add Critical Warning or Contraindication" onClick={popup.openAdd} />

                                <UDIRowPopup
                                    isOpen={popup.isOpen}
                                    title={editingIndex !== null ? "Edit Critical Warning or Contraindication" : "Add Critical Warning or Contraindication"}
                                    saveLabel={editingIndex !== null ? "Save" : "Add"}
                                    onClose={popup.close}
                                    onSave={handleSaveItem}
                                    saveDisabled={!!currentTypeError}
                                >
                                    <UDIPopupField label="Type">
                                        <select value={currentType.Value} onChange={handleTypeChange}>
                                            {cwctype.map(type => <option key={type.key} value={type.key}>{type.value}</option>)}
                                        </select>
                                    </UDIPopupField>
                                    <UDIPopupField label={`Descriptions${currentType.Value === CRITICAL_WARNING_OTHER ? " *" : ""}`}>
                                        {currentTypeError && (
                                            <div><span className="form-field-error-message">
                                                {currentTypeError}
                                            </span></div>
                                        )}
                                        <table className="table table-bordered">
                                            <thead>
                                                <tr>
                                                    <th>Description</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {currentDescriptions.length === 0 && (
                                                    <tr><td colSpan="2" style={{ textAlign: 'center', color: '#888' }}>No descriptions added yet</td></tr>
                                                )}
                                                {currentDescriptions.map((desc, index) => {
                                                    const lang = desc.Description?.Language?.Text || '';
                                                    const text = desc.Description?.Value || '';
                                                    return (
                                                        <tr key={index}>
                                                            <td style={{ whiteSpace: 'pre-wrap' }}>{lang ? `${lang}: ${text}` : text}</td>
                                                            <td>
                                                                <button type="button" className="udiRemoveButton" onClick={() => handleRemoveDescription(index)}>
                                                                    <i className="fas fa-times-circle"></i>
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                        <select value={descLanguage.Value} onChange={handleDescLanguageChange} style={{ marginBottom: '5px' }}>
                                            {languages.map(lang => <option key={lang.key} value={lang.key}>{lang.value}</option>)}
                                        </select>
                                        <textarea value={descText} onChange={(e) => setDescText(e.target.value)} rows={2} />
                                        <input type="button" className="udiActionButton" value="Add Description" onClick={handleAddDescription} style={{ marginTop: '5px' }} />
                                    </UDIPopupField>
                                </UDIRowPopup>

                                <input type="hidden" name={hiddenFieldName} value={JSON.stringify(items)} />
                            </div>

                            :


                            <>

                                <table className="dataTable" style={{ borderWidth: '1px', width: '100%' }}>
                                    <thead>
                                        <tr>
                                            <th>Type</th>
                                            <th>Descriptions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {items.map((item, index) => (
                                            <tr key={index}>
                                                <td>{item.Type?.Text}</td>
                                                <td>
                                                    <ul style={{ margin: 0, paddingLeft: '20px' }}>
                                                        {(item.Descriptions || []).map((descItem, dIndex) => {
                                                            const lang = descItem.Description?.Language?.Text || '';
                                                            const text = descItem.Description?.Value || '';
                                                            return (
                                                                <li key={dIndex} style={{ whiteSpace: 'pre-wrap' }}>
                                                                    {lang ? `${lang}: ${text}` : text}
                                                                </li>
                                                            );
                                                        })}
                                                    </ul>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>

                                {invalidItems.length > 0 && (
                                    <span className="form-field-error-message">
                                        {otherWarningError(invalidItems[0].Descriptions)}
                                    </span>
                                )}

                                {/*
						        <input
							        type="button"
							        className="udiActionButton"
							        value="Edit"
							        onClick={() => changeIsEditingField(true)}
						        />
                                */}


                            </>

                        }

                    </div>

                </div>
            </div>
        </div>


    );
};

export default CriticalWarningsOrContraindications;